// src/stores/bookmark.store.ts
import { create } from 'zustand';
import type { BookmarkItem, BookmarkListResponse, BookmarkPagination } from '../models/bookmark.model.ts';

interface BookmarkStoreState {
  bookmarkedServiceUuids: Set<string>;
  bookmarkList: BookmarkItem[];
  pagination: BookmarkPagination | null;

  listLoading: boolean;
  listError: string | null;
  syncLoading: boolean;
  togglingUuids: Set<string>;

  isBookmarked: (uuid: string) => boolean;
  setSyncedUuids: (uuids: string[]) => void;
  toggleOptimistic: (uuid: string) => void;
  setToggling: (uuid: string, v: boolean) => void;
  setBookmarkList: (response: BookmarkListResponse, append: boolean) => void;
  removeFromList: (uuid: string) => void;
  setListLoading: (v: boolean) => void;
  setListError: (msg: string | null) => void;
  setSyncLoading: (v: boolean) => void;
  clear: () => void;
}

const emptyState = {
  bookmarkedServiceUuids: new Set<string>(),
  bookmarkList: [] as BookmarkItem[],
  pagination: null as BookmarkPagination | null,
  listLoading: false,
  listError: null as string | null,
  syncLoading: false,
  togglingUuids: new Set<string>(),
};

export const useBookmarkStore = create<BookmarkStoreState>((set, get) => ({
  ...emptyState,

  isBookmarked: (uuid) => get().bookmarkedServiceUuids.has(uuid),

  setSyncedUuids: (uuids) =>
    set({ bookmarkedServiceUuids: new Set(uuids) }),

  toggleOptimistic: (uuid) =>
    set((state) => {
      const next = new Set(state.bookmarkedServiceUuids);
      if (next.has(uuid)) {
        next.delete(uuid);
      } else {
        next.add(uuid);
      }
      return { bookmarkedServiceUuids: next };
    }),

  setToggling: (uuid, v) =>
    set((state) => {
      const next = new Set(state.togglingUuids);
      if (v) next.add(uuid);
      else next.delete(uuid);
      return { togglingUuids: next };
    }),

  setBookmarkList: (response, append) =>
    set((state) => ({
      bookmarkList: append
        ? [...state.bookmarkList, ...(response.bookmarks ?? [])]
        : response.bookmarks ?? [],
      pagination: response.pagination ?? null,
    })),

  removeFromList: (uuid) =>
    set((state) => ({
      bookmarkList: state.bookmarkList.filter((b) => b.uuid !== uuid),
    })),

  setListLoading: (v) => set({ listLoading: v }),
  setListError: (msg) => set({ listError: msg }),
  setSyncLoading: (v) => set({ syncLoading: v }),

  clear: () =>
    set({
      ...emptyState,
      bookmarkedServiceUuids: new Set<string>(),
      togglingUuids: new Set<string>(),
    }),
}));
